import Tracker, { type Options as TrackerOptions } from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import { createGraphqlMiddleware } from '@openreplay/tracker-graphql';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
} from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { E2E_TEST_USER_SHARED_NAME } from '@revelio/core/e2e-testing-users';

import { environment } from '../../environments/environment';
import { previewJson } from './preview-json';

const isLocalHost = window.location.hostname === 'localhost';
const isProd = environment.production;

type NetworkOptions = NonNullable<TrackerOptions['network']>;
type NetworkSanitizer = NonNullable<NetworkOptions['sanitizer']>;
type RequestResponseData = Parameters<NetworkSanitizer>[0];

const tracker = new Tracker({
  __DISABLE_SECURE_MODE: isLocalHost, // for local use only
  projectKey: environment.OPEN_REPLAY_PROJECT_KEY, // this key is an empty string for all environments besides local dev and prod
  crossdomain: {
    enabled: true,
  },
  captureIFrames: true,
  defaultInputMode: 0,
  obscureInputDates: false,
  obscureInputNumbers: false,
  obscureInputEmails: false,
  obscureTextNumbers: false,
  obscureTextEmails: false,
  captureExceptions: true,
  capturePerformance: true,
  capturePageLoadTimings: true,
  capturePageRenderTimings: true,
  network: {
    useProxy: !isLocalHost,
    sessionTokenHeader: false,
    failuresOnly: false,
    ignoreHeaders: false,
    capturePayload: true,
    captureInIframes: true,
    sanitizer: (data: RequestResponseData) => {
      const url = new URL(data.url);
      // if not in allowed open replay hosts, remove request and response body
      // if (!openReplayAllowedHosts.includes(url.origin)) {
      //   data.request.body = null;
      //   data.response.body = null;
      // }
      // if download request, remove response body
      if (url.pathname.includes('/download')) {
        data.response.body = null;
      }

      // Truncate large response bodies
      if (data.response.body) {
        data.response.body = previewJson(data.response.body);
      }

      return data;
    },
  },
});

const recordGraphQL = tracker.use(createGraphqlMiddleware());

export const recordTruncatedGraphQL = (
  kind: string,
  operationName: string,
  variables: unknown,
  data: unknown,
  duration: number
) => {
  const truncatedData = previewJson(data);
  return recordGraphQL(kind, operationName, variables, truncatedData, duration);
};

interface OpenReplayState {
  tracker: Tracker;
  recordGraphQL: typeof recordGraphQL;
}

const OpenReplayContext = createContext<OpenReplayState>({
  tracker,
  recordGraphQL,
});

export const OpenReplayProvider = ({ children }: PropsWithChildren) => {
  const { loggedInUser } = useGetLoggedInUser();

  useEffect(() => {
    const isTestUser = loggedInUser?.email?.includes(E2E_TEST_USER_SHARED_NAME);

    // checking we have user email so that we don't start tracking until we're sure it's not a test user session
    if (isProd && loggedInUser?.email && !isTestUser) {
      tracker.use(trackerAssist());
      tracker.start();
    }

    return () => {
      if (isProd) tracker.stop();
    };
  }, [loggedInUser?.email]);

  return (
    <OpenReplayContext.Provider value={{ tracker, recordGraphQL }}>
      {children}
    </OpenReplayContext.Provider>
  );
};

export const useOpenReplay = () => useContext(OpenReplayContext);
